// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import { IPaginateApiResponse } from 'common/api/resources/Response';

export interface IUseLoadMoreOptions<T> {
    fetchPage: (page: number) => Promise<{ data: IPaginateApiResponse<T[]> }>;
    enabled?: boolean;
    onFirstPage?: (items: T[]) => void;
}

export interface IUseLoadMoreResult<T> {
    items: T[];
    isLoading: boolean;
    hasMore: boolean;
    loadMore: () => void;
}

export const useLoadMore = <T>(
    {
        fetchPage,
        enabled = true,
        onFirstPage,
    }: IUseLoadMoreOptions<T>,
    deps: React.DependencyList
): IUseLoadMoreResult<T> => {
    const [items, setItems] = React.useState<T[]>([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [page, setPage] = React.useState(1);
    const [hasMore, setHasMore] = React.useState(false);

    const fetchPageRef = React.useRef(fetchPage);
    fetchPageRef.current = fetchPage;
    const onFirstPageRef = React.useRef(onFirstPage);
    onFirstPageRef.current = onFirstPage;

    const load = React.useCallback(async (pageNum: number, append: boolean) => {
        setIsLoading(true);
        try {
            const response = await fetchPageRef.current(pageNum);
            const data = response.data.data;
            setItems(prev => append ? [...prev, ...data] : data);
            setHasMore(!!response.data.links.next);
            if (pageNum === 1 && onFirstPageRef.current) {
                onFirstPageRef.current(data);
            }
        } catch {
            // Error is handled by the API layer.
        } finally {
            setIsLoading(false);
        }
    }, []);

    React.useEffect(() => {
        if (!enabled) {
            return;
        }
        setPage(1);
        load(1, false);
        // `deps` drives reload; `load` is stable and intentionally omitted.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, ...deps]);

    const loadMore = React.useCallback(() => {
        if (isLoading) {
            return;
        }
        const nextPage = page + 1;
        setPage(nextPage);
        load(nextPage, true);
    }, [isLoading, page, load]);

    return {
        items,
        isLoading,
        hasMore,
        loadMore,
    };
};
